import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PersonAdd, Search } from "@mui/icons-material";
import { addOneOffSessionParticipant } from "./api";
import type { Club, Handler, OneOffSession } from "./types";
import DialogHeader from "./DialogHeader";
import { MemberContent } from "./MemberListItem";

interface AddOneOffParticipantDialogProps {
  target: { club: Club; session: OneOffSession } | null;
  handlers: Handler[];
  handlersLoading: boolean;
  onEnsureHandlersLoaded: () => void;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const MIN_SEARCH_LENGTH = 5;

const AddOneOffParticipantDialog: React.FC<AddOneOffParticipantDialogProps> = ({
  target,
  handlers,
  handlersLoading,
  onEnsureHandlersLoaded,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (target) {
      setTab(0);
      setSearchQuery("");
      setFirstName("");
      setLastName("");
      setEmail("");
      onEnsureHandlersLoaded();
    }
  }, [target, onEnsureHandlersLoaded]);

  const existingHandlerIds = new Set(
    target?.session.participants.map((p) => p.handler_id) ?? [],
  );

  const query = searchQuery.toLowerCase();
  const filteredHandlers = handlers.filter(
    (h) =>
      (h.handler_name.toLowerCase().includes(query) ||
        h.email_address.toLowerCase().includes(query)) &&
      !existingHandlerIds.has(h.handler_id),
  );

  const showResults = searchQuery.trim().length >= MIN_SEARCH_LENGTH;

  const handleAddExisting = async (handlerId: number) => {
    if (!target) return;
    try {
      setAdding(true);
      await addOneOffSessionParticipant({
        one_off_session_id: target.session.id,
        handler_id: handlerId,
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error adding participant:", err);
      onError("Failed to add participant. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const handleAddNew = async () => {
    if (!target) return;
    try {
      setAdding(true);
      await addOneOffSessionParticipant({
        one_off_session_id: target.session.id,
        email_address: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error adding participant:", err);
      onError("Failed to add participant. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const canAddNew =
    firstName.trim() !== "" && lastName.trim() !== "" && email.trim() !== "";

  return (
    <Dialog
      open={target != null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader
        title="Add Participant"
        subtitle={target?.session.name}
        onClose={onClose}
        closeDisabled={adding}
      />
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mb: 2 }}
          variant="fullWidth"
        >
          <Tab label="Search existing" />
          <Tab label="Add someone new" />
        </Tabs>

        {tab === 0 ? (
          <>
            <TextField
              fullWidth
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              helperText={
                !showResults
                  ? `Enter at least ${MIN_SEARCH_LENGTH} characters to search`
                  : undefined
              }
              InputProps={{
                startAdornment: (
                  <Search sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{ mb: 2 }}
            />
            {!showResults ? null : handlersLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredHandlers.length === 0 ? (
              <Alert severity="info">
                No handlers found matching your search.
              </Alert>
            ) : (
              <List
                sx={{ maxHeight: { xs: "none", sm: 400 }, overflow: "auto" }}
              >
                {filteredHandlers.map((handler) => (
                  <ListItemButton
                    key={handler.handler_id}
                    onClick={() => handleAddExisting(handler.handler_id)}
                    disabled={adding}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <MemberContent
                      member={handler}
                      avatarColor={theme.palette.primary.main}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info">
              Add someone who is not yet on the platform. They will receive an
              invoice by email and do not need to be a club member.
            </Alert>
            <TextField
              fullWidth
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={adding}
            />
            <TextField
              fullWidth
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={adding}
            />
            <TextField
              fullWidth
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={adding}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={adding}>
          Cancel
        </Button>
        {tab === 1 && (
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleAddNew}
            disabled={adding || !canAddNew}
          >
            {adding ? "Adding…" : "Add Participant"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddOneOffParticipantDialog;
